import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CheckCircle2, Loader2, QrCode, Save, WifiOff } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import QrScanner from '../components/QrScanner.jsx';
import {
  EXPENSE_TYPES,
  PAYMENT_TYPES,
  createExpense,
  lookupZimraInvoice,
  uploadExpenseDocument,
} from '../api/expenses';
import {
  enqueueExpense,
  pendingCount,
  syncOutbox,
} from '../offline/outbox';
import { compressImage } from '../utils/image';
import { detectAndCrop } from '../utils/document-scan';
import { VAT_RATE } from '../constants/vat';

// Best-effort fiscal-number extraction from a ZIMRA QR URL. ZIMRA
// fiscal-device QRs typically encode a verification URL like:
//   https://fdms.zimra.co.zw/.../?fiscal=NNNNNNNNN
//   https://invoice-verification.zimra.co.zw/?...
// We scan the URL for fiscal_number / fiscalNumber / fiscal=… variants
// first, fall back to the longest digit run (≥6 digits) which is what
// fiscal numbers look like. If nothing matches we still record the raw
// URL — admins can reconcile from there.
function extractFiscalNumber(qrValue) {
  if (!qrValue) return null;
  try {
    // Try keyed query/path params first
    const m = qrValue.match(
      /(?:fiscal[_-]?number|fiscalNumber|fiscal|invoice[_-]?no|receiptNumber)[=/]([A-Za-z0-9-]+)/i,
    );
    if (m) return m[1];
    // Fallback — longest digit run with at least 6 digits
    const digits = qrValue.match(/\d{6,}/g);
    if (digits) return digits.sort((a, b) => b.length - a.length)[0];
  } catch {
    // Defensive — never let the parser throw out of this function.
  }
  return null;
}

/**
 * Capture Expense flow.
 *
 * Layout:
 *   - "Take photo" / "Choose photo" button — fires a hidden <input type="file"
 *     accept="image/*" capture="environment">.
 *   - On photo selected → show thumbnail + run Tesseract.js OCR.
 *   - OCR fields pre-fill the form (amount, date, vendor). User can edit.
 *   - Manual entry path also available — user can skip the photo entirely.
 *   - Submit:
 *       online  → POST /expenses + POST /expenses/{id}/document.
 *       offline → enqueue in IndexedDB outbox; auto-sync on next 'online'.
 *
 * Per docs/PWA_BRIEF.md §2.4 + §12: Tesseract accuracy is uneven on real
 * receipts; pre-filled fields are highlighted but always editable.
 */
export default function CaptureExpense() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Two flavours of the captured photo:
  //   photoOriginal — the compressed-but-uncropped JPEG
  //   photoDetected — the perspective-corrected crop, when edge
  //                   detection succeeded; null when it failed or
  //                   hasn't run yet
  // The rep can toggle between them via `useDetectedPhoto`. Submit
  // uses whichever the toggle resolves to (see effectivePhotoBlob).
  const [photoOriginal, setPhotoOriginal] = useState(null);
  const [photoDetected, setPhotoDetected] = useState(null);
  const [useDetectedPhoto, setUseDetectedPhoto] = useState(true);
  const [photoFilename, setPhotoFilename] = useState(null);

  // Stage of the photo pipeline: 'compress' | 'detect' | null. Drives
  // the spinner overlay text on the preview so the rep can see what
  // the app's doing.
  const [photoStage, setPhotoStage] = useState(null);

  // The blob that actually gets uploaded — derived from the toggle.
  // Falls back to the original if no detected version exists yet (or
  // if the rep flipped the toggle off).
  const photoBlob = useMemo(
    () => (useDetectedPhoto && photoDetected ? photoDetected : photoOriginal),
    [useDetectedPhoto, photoDetected, photoOriginal],
  );

  // Object URL for the preview image. Allocated when photoBlob
  // changes, revoked on next change / unmount so we don't leak.
  const [photoPreview, setPhotoPreview] = useState(null);
  useEffect(() => {
    if (!photoBlob) {
      setPhotoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(photoBlob);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoBlob]);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [vendor, setVendor] = useState('');
  // Type of expense — Operating / COGS / VAT Payment. Replaces the
  // older fine-grained Category dropdown on mobile. Default 'operating'
  // because that's the most common rep-captured expense (fuel, travel,
  // office bits).
  const [expenseType, setExpenseType] = useState('operating');
  // Payment method (cash / bank transfer / etc). Default cash because
  // that's the most common in-the-field tender.
  const [paymentType, setPaymentType] = useState('cash');
  // True when the entered amount already includes VAT — drives the
  // vat_amount / amount_ex_vat split on the submit payload.
  const [vatIncluded, setVatIncluded] = useState(false);

  // ZIMRA fiscalised-invoice fields. Populated when the rep scans a
  // QR off a fiscalised receipt. Both go onto the submit payload as
  // zimra_qr_url / zimra_fiscal_number — desktop reports key off these
  // for reconciliation against the ZIMRA system.
  const [zimraQrUrl, setZimraQrUrl] = useState(null);
  const [zimraFiscalNumber, setZimraFiscalNumber] = useState(null);

  // Whether the QR scanner overlay is open.
  const [scannerOpen, setScannerOpen] = useState(false);

  // True while the ZIMRA lookup is in flight after a successful scan.
  // Drives a small "Looking up…" indicator on the success card so the
  // rep knows the form is about to auto-populate.
  const [lookupRunning, setLookupRunning] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous double-tap guard — closes the gap between tap and
  // the `submitting` state update where a second tap could fire.
  const submitInFlight = useRef(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [outboxCount, setOutboxCount] = useState(0);

  // Track online/offline + outbox depth.
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      // Auto-drain outbox on reconnect — best-effort.
      syncOutbox().then(refreshOutboxCount);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    refreshOutboxCount();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  async function refreshOutboxCount() {
    try {
      const n = await pendingCount();
      setOutboxCount(n);
    } catch {
      // Ignore — outbox count is informational.
    }
  }

  async function onPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show the raw file immediately so the rep gets instant visual
    // feedback. The compressed + detected versions replace it as the
    // pipeline progresses.
    setPhotoFilename(file.name || `receipt-${Date.now()}.jpg`);
    setPhotoOriginal(file);
    setPhotoDetected(null);
    setUseDetectedPhoto(true); // default to the detected crop when it lands
    setError(null);

    // Stage 1 — compress. Photos straight off a phone camera are 3-5MB;
    // we rescale to longest-edge 1600px at JPEG q=0.75, landing
    // around 200-400KB. The original-file check inside compressImage
    // skips the work if it's already small. NO OCR pre-fill — that's
    // QR-only on this app now.
    setPhotoStage('compress');
    let compressed = file;
    try {
      compressed = await compressImage(file, { maxDim: 1600, quality: 0.75 });
      setPhotoOriginal(compressed);
    } catch (err) {
      // Receipt record always wins over compression bandwidth — keep
      // the original file blob if compression fails.
      // eslint-disable-next-line no-console
      console.warn('Compression failed:', err);
    }

    // Stage 2 — auto edge-detect. Lazy-loads OpenCV.js on first call
    // (~2.5MB), so this stage may take a few seconds the first time
    // a rep scans. Subsequent captures reuse the loaded library.
    // detectAndCrop returns null on any failure — silent fallback to
    // the original.
    setPhotoStage('detect');
    try {
      const cropped = await detectAndCrop(compressed, { maxOutputDim: 1600, quality: 0.8 });
      if (cropped) setPhotoDetected(cropped);
    } catch (err) {
      // Defensive — detectAndCrop already swallows errors.
      // eslint-disable-next-line no-console
      console.warn('Edge detection failed:', err);
    } finally {
      setPhotoStage(null);
    }
  }

  /**
   * Apply a QR scan — store the URL, extract a fiscal number from
   * URL params as an immediate placeholder, flip vat_included on,
   * close the scanner, then fire the backend ZIMRA lookup to enrich
   * the form with vendor / amount / date.
   *
   * The lookup is best-effort: if it fails (network blip, ZIMRA
   * down, page layout changed), we keep the fiscal number and the
   * rep types the rest. The scan was still a win.
   */
  async function onQrScan(rawValue) {
    setZimraQrUrl(rawValue);
    setVatIncluded(true);

    // Immediate placeholder fiscal number from the URL params (or
    // the regex fallback). The backend lookup will overwrite this
    // with the canonical INVOICE NUMBER from the verification page
    // if it succeeds.
    const fallbackFiscal = extractFiscalNumber(rawValue);
    if (fallbackFiscal) setZimraFiscalNumber(fallbackFiscal);

    setScannerOpen(false);

    // Fire the backend lookup. lookupZimraInvoice swallows failures
    // and returns null — no need to wrap in try/catch here.
    setLookupRunning(true);
    const data = await lookupZimraInvoice(rawValue);
    setLookupRunning(false);
    if (!data || !data.valid) return;

    // Pre-fill from the verification page. We only overwrite fields
    // the rep hasn't already typed into — defensive against a
    // late-arriving lookup clobbering manual entry.
    if (data.fiscal_number) setZimraFiscalNumber(data.fiscal_number);
    if (data.trade_name && !name) setName(data.trade_name);
    if (data.trade_name && !vendor) setVendor(data.trade_name);
    if (data.amount != null && !amount) setAmount(String(data.amount));
    if (data.invoice_date && date === new Date().toISOString().slice(0, 10)) {
      setDate(data.invoice_date);
    }
  }

  function clearQr() {
    setZimraQrUrl(null);
    setZimraFiscalNumber(null);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (!name.trim()) {
      setError('Give the expense a name.');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError('Enter an amount greater than 0.');
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);

    // VAT split — when the rep ticked "Includes VAT", the entered
    // amount is gross. Compute amount_ex_vat / vat_amount so the
    // backend (and downstream desktop reports) get the breakdown
    // without re-deriving. When VAT is excluded the breakdown is
    // trivial (ex_vat == amount, vat == 0).
    const grossAmount = Number(amount);
    const amountExVat = vatIncluded
      ? grossAmount / (1 + VAT_RATE)
      : grossAmount;
    const vatAmount = vatIncluded
      ? grossAmount - amountExVat
      : 0;

    const payload = {
      expense_name: name.trim(),
      amount: grossAmount,
      date: new Date(date).toISOString(),
      vendor: vendor || null,
      // category is required by the backend schema (defaults to 'other'
      // server-side too) — mobile no longer surfaces the fine-grained
      // category picker, so we always send 'other' and let the desktop
      // ERP reclassify if needed.
      category: 'other',
      expense_type: expenseType,
      payment_type: paymentType,
      payment_status: 'paid',
      vat_included: vatIncluded,
      amount_ex_vat: amountExVat,
      vat_amount: vatAmount,
      // ZIMRA fiscalisation — when the rep scanned a QR these are
      // populated; otherwise null. Backend stores both for compliance
      // / reconciliation reporting on desktop.
      zimra_qr_url: zimraQrUrl,
      zimra_fiscal_number: zimraFiscalNumber,
    };

    try {
      if (online) {
        // Direct submit. Use crypto.randomUUID() as Idempotency-Key so a
        // mid-request connection drop and a manual retry don't double-create.
        const idempotencyKey = crypto.randomUUID();
        const created = await createExpense(payload, { idempotencyKey });
        if (photoBlob) {
          const docKey = crypto.randomUUID();
          try {
            await uploadExpenseDocument(
              created.id,
              photoBlob,
              photoFilename || 'receipt.jpg',
              { idempotencyKey: docKey },
            );
          } catch (uploadErr) {
            // Expense created but document upload failed — surface but don't
            // queue (would create a duplicate expense on outbox sync). User
            // can re-attach later from the desktop ERP.
            console.warn('Document upload failed:', uploadErr);
          }
        }
        navigate('/', {
          replace: true,
          state: { flash: `Expense "${created.expense_name}" saved` },
        });
      } else {
        // Offline → enqueue.
        await enqueueExpense({
          payload,
          photoBlob,
          photoFilename: photoFilename || 'receipt.jpg',
        });
        await refreshOutboxCount();
        navigate('/', {
          replace: true,
          state: {
            flash: 'Saved offline. Will sync when you’re back online.',
          },
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title="Capture Expense"
        subtitle="Scan a receipt or enter manually"
        icon={Camera}
      />

      {!online && (
        <div role="status" className="mb-4 mb-card bg-amber-50 border border-amber-200 flex items-center gap-2">
          <WifiOff className="h-5 w-5 text-amber-700" aria-hidden="true" />
          <p className="text-small text-amber-900">
            Offline — this expense will queue and sync when you’re back online.
          </p>
        </div>
      )}

      {outboxCount > 0 && (
        <p className="mb-3 text-small text-amber-700">
          {outboxCount} expense{outboxCount === 1 ? '' : 's'} waiting to sync.
        </p>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Capture options — two paths.
              1) Scan ZIMRA QR on a fiscalised invoice (high-confidence
                 path: extracts fiscal number exactly, no OCR guessing).
              2) Take photo of receipt (fallback for non-fiscalised
                 receipts; image is attached as record-keeping, OCR
                 runs best-effort to pre-fill amount/date/vendor).
            Either path can be used; both can populate the same form. */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhotoChange}
            className="hidden"
          />

          {!photoPreview && !zimraFiscalNumber && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                className="w-full min-h-touch rounded-card bg-amber-500 active:bg-amber-600 text-white shadow-lg p-4 flex items-center gap-3"
              >
                <span className="w-10 h-10 rounded-card bg-white/15 flex items-center justify-center shrink-0">
                  <QrCode className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-body font-semibold">Scan fiscalised invoice</p>
                  <p className="text-caption text-white/80">
                    Reads the ZIMRA QR · captures the fiscal number exactly
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full min-h-touch rounded-card border-2 border-dashed border-gray-300 p-4 flex items-center gap-3 text-left"
              >
                <span className="w-10 h-10 rounded-card bg-gray-100 flex items-center justify-center shrink-0">
                  <Camera className="h-5 w-5 text-gray-600" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-body font-medium text-gray-900">Take photo of receipt</p>
                  <p className="text-caption text-gray-500">
                    For non-fiscalised receipts · attached as record
                  </p>
                </div>
              </button>
            </div>
          )}

          {/* Scanned-QR confirmation — minimal "successfully scanned"
              state. Fiscal number sits as a small chip; the raw URL is
              dropped from the visible UI (it gets sent in the payload
              still). VAT-toggle auto-flip is communicated by the
              checkbox below being already ticked, so we don't repeat
              that here. */}
          {zimraFiscalNumber && (
            <div className="rounded-card bg-green-50 border border-green-200 p-3 flex items-center gap-3">
              {lookupRunning ? (
                <Loader2 className="h-5 w-5 text-green-700 shrink-0 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-700 shrink-0" aria-hidden="true" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-small font-semibold text-green-900">
                  {lookupRunning ? 'Looking up details…' : 'Successfully scanned'}
                </p>
                <p className="text-caption text-green-800 font-mono truncate">
                  {zimraFiscalNumber}
                </p>
              </div>
              <button
                type="button"
                onClick={clearQr}
                disabled={lookupRunning}
                className="text-caption text-green-800 underline min-h-touch shrink-0 disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          )}

          {photoPreview && (
            <div className="mt-2 space-y-2">
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Receipt preview"
                  className="w-full max-h-72 object-contain rounded-card bg-gray-100"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-2 right-2 bg-surface text-amber-700 border border-amber-500 rounded-pill px-3 py-1 text-small font-medium"
                >
                  Re-take
                </button>
                {photoStage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-canvas/70 rounded-card">
                    <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
                    <span className="ml-2 text-small text-gray-700">
                      {photoStage === 'detect'
                        ? 'Detecting edges…'
                        : 'Processing receipt…'}
                    </span>
                  </div>
                )}
              </div>

              {/* Cropped/original toggle — only renders once edge
                  detection has produced a usable crop. The default
                  is the cropped version (most rep-friendly) but the
                  rep can flip back if the auto-crop got it wrong. */}
              {photoDetected && (
                <div className="flex items-center justify-between gap-3 px-1">
                  <p className="text-caption text-gray-600">
                    {useDetectedPhoto
                      ? 'Auto-cropped to receipt edges'
                      : 'Showing original photo'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setUseDetectedPhoto((v) => !v)}
                    className="text-caption text-amber-700 underline min-h-touch"
                  >
                    {useDetectedPhoto ? 'Use original' : 'Use cropped'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <FormField
          label="Expense name"
        >
          <TextInput
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Fuel — sales trip"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Amount"
          >
            <TextInput
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FormField>

          <FormField
            label="Date"
          >
            <TextInput
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </FormField>
        </div>

        {/* Includes-VAT toggle sits right under the Amount/Date row
            because that's where it's contextually relevant — the
            decision is "what does the amount above represent?". When
            on, a small breakdown row shows ex-VAT and VAT amounts so
            the rep can see exactly what's getting recorded. */}
        <FormField label="Includes VAT">
          <label className="flex items-center gap-3 min-h-touch select-none">
            <input
              type="checkbox"
              checked={vatIncluded}
              onChange={(e) => setVatIncluded(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-body text-gray-900">
              Amount above includes {(VAT_RATE * 100).toFixed(1)}% VAT
            </span>
          </label>
          {vatIncluded && Number(amount) > 0 && (
            <div className="mt-2 px-3 py-2 rounded-card bg-amber-50 border border-amber-200 space-y-0.5">
              <div className="flex justify-between text-caption text-amber-900">
                <span>Ex VAT</span>
                <span>{(Number(amount) / (1 + VAT_RATE)).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-caption text-amber-900">
                <span>VAT</span>
                <span>{(Number(amount) - Number(amount) / (1 + VAT_RATE)).toFixed(2)}</span>
              </div>
            </div>
          )}
        </FormField>

        <FormField label="Vendor (optional)">
          <TextInput
            type="text"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
          />
        </FormField>

        <FormField label="Type of expense">
          <select
            value={expenseType}
            onChange={(e) => setExpenseType(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md text-body bg-surface focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            {EXPENSE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Payment method">
          <select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md text-body bg-surface focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            {PAYMENT_TYPES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </FormField>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Saving…' : online ? 'Save Expense' : 'Queue for sync'}
          </SaveButton>
        </SaveBar>
      </form>

      {/* QR scanner overlay — mounted only when open so the camera
          stream is fully released when closed (the component's cleanup
          stops every track on unmount). */}
      {scannerOpen && (
        <QrScanner
          onScan={onQrScan}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </main>
  );
}
